import { CustomerAccountService } from '@amzn/awscat-portfolio-assessment-service-client';
import { CustomerAccount } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, withAuthContext } from '@amzn/awscat-react-components';
import { Autosuggest, AutosuggestProps, Button, Container, FormField, Header, Input, Modal, NonCancelableCustomEvent,
    RadioGroup,RadioGroupProps,SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { isAWSUser, isCognito } from '../../common/util';
import { accountOptionsItems, emptyAccount, maxCustomerAccounts } from './constants';
import CustomerAccountPanel from './CustomerAccountPanel';
import { AccountOptions } from './interface';

type Props = AuthContextInterface & {
    account: CustomerAccount;
    setAccount: (account: CustomerAccount) => void;
}
const customerAccountService = new CustomerAccountService();
const CustomerAccountSettings: FunctionComponent<Props> = ({account, setAccount, auth}) => {
    const [panelVisible, setPanelVisible] = useState(false);
    const [accountOptions, setAccountOptions] = useState(accountOptionsItems);
    const [accountOption, setAccountOption] = useState(AccountOptions.USE_EXISTING);
    const [accounts, setAccounts] = useState<CustomerAccount[]>([]);
    const [accountItems, setAccountItems] = useState<AutosuggestProps.Options>([]);
    const [status, setStatus] = useState<AutosuggestProps.StatusType>('finished');
    const [value, setValue] = useState('');

    useEffect(() => {
        async function prepareAccountOptions() {
            const isCognitoUser: boolean = await isCognito(auth);
            if (isCognitoUser) {
                setAccountOptions(accountOptionsItems.filter(item => item.value === AccountOptions.CREATE_NEW));
                setAccountOption(AccountOptions.CREATE_NEW);
            }
        }
        prepareAccountOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (isAWSUser(auth)) {
        return (
            <Container header={<Header variant="h2">{constants.CREATE_PORTFOLIO_ACCOUNT_SETTINGS}</Header>}>
                <SpaceBetween size="l">        
                    <Button iconName="add-plus" onClick={() => setPanelVisible(true)}>{constants.SELECT_FROM_SALESFORCE}</Button>
                    <Modal visible={panelVisible} onDismiss={() => setPanelVisible(false)} header={constants.SEARCH_SALESFORCE} size="large">
                        <CustomerAccountPanel setAccount={setAccount} account={account} hidePanel={() => setPanelVisible(false)} visible={panelVisible}/>
                    </Modal>
                    <FormField
                        description={constants.CUSTOMER_ACCOUNT_NAME_DESP}
                        label={constants.CUSTOMER_ACCOUNT_NAME}>
                        <Input
                            disabled
                            placeholder={constants.CUSTOMER_ACCOUNT_NAME_PLACEHOLDER}
                            value={account?.accountName}
                        />
                    </FormField>
                    <FormField
                        description={constants.SALESFORCE_DESP}
                        label={constants.SALESFORCE_ID}>
                        <Input
                            disabled
                            placeholder={constants.SALESFORCE_ID_PLACEHOLDER}
                            value={account?.refId}
                        />
                    </FormField>
                </SpaceBetween>
            </Container>
        );
    } else {
        function onAccountOptionChange(event: NonCancelableCustomEvent<RadioGroupProps>) {
            setAccountOption(event.detail.value as AccountOptions);
            setAccount(emptyAccount);
        }
        async function getAllAccountsForPartner(filteringText: string) {
            setStatus('loading');
            setAccountItems([]);
            const accounts = await customerAccountService.list(maxCustomerAccounts, filteringText);
            const accountItems: AutosuggestProps.Options = accounts.map(accountOption => ({
                value: accountOption.accountId,
                label: accountOption.accountName,
            }));
            setAccounts(accounts);
            setAccountItems(accountItems);
            setStatus('finished');
        }
        function onAccountNameChange(event: NonCancelableCustomEvent<AutosuggestProps.ChangeDetail>) {
            setValue(event.detail.value);
            const selectedAccount = accounts.find(acc => acc.accountId === event.detail.value);
            if (!selectedAccount) {
                setAccount(emptyAccount);
                return;
            }
            const {refId, accountName, accountId} = selectedAccount;
            setAccount({...account, accountId, accountName, refId});
            setValue(accountName);
        }
        let accountNameField = null;
        if (accountOption === AccountOptions.CREATE_NEW) {
            accountNameField = (
                <Input
                    placeholder={constants.CUSTOMER_ACCOUNT_NAME_PLACEHOLDER}
                    value={account?.accountName}
                    onChange={event => setAccount({...account, accountName: event.detail.value})}
                />
            );
        } else {
            accountNameField = (
                <Autosuggest
                    enteredTextLabel={value => `Use ${value}`}
                    options={accountItems}
                    placeholder={constants.CUSTOMER_ACCOUNT_NAME_PLACEHOLDER}
                    value={value}
                    onChange={onAccountNameChange}
                    onLoadItems={({ detail }) => getAllAccountsForPartner(detail.filteringText)}
                    empty={constants.ACCOUNT_NO_MATCH_FOUND}
                    loadingText={constants.ACCOUNT_LOADING}
                    statusType={status}
                    filteringType='manual'
                />
            );
        }
        return (
            <Container header={<Header variant="h2">{constants.CREATE_PORTFOLIO_ACCOUNT_SETTINGS}</Header>}>
                <SpaceBetween size="l">
                    <FormField>
                        <RadioGroup
                            onChange={onAccountOptionChange}
                            value={accountOption}
                            items={accountOptions} />
                    </FormField>
                    <FormField
                        description={constants.CUSTOMER_ACCOUNT_NAME_DESP}
                        label={constants.CUSTOMER_ACCOUNT_NAME}>
                        {accountNameField}
                    </FormField>
                    <FormField
                        description={constants.ACCOUNT_REFERENCE_ID_DESP}
                        label={constants.ACCOUNT_REFERENCE_ID}>
                        <Input
                            disabled={accountOption === AccountOptions.USE_EXISTING}
                            placeholder={constants.ACCOUNT_REFERENCE_ID_PLACEHOLDER}
                            value={account?.refId}
                            onChange={event => setAccount({...account, refId: event.detail.value})}
                        />
                    </FormField>
                </SpaceBetween>
            </Container>
        );
    }
};

export default withAuthContext(CustomerAccountSettings);
